import React from 'react';
import { makeStyles } from "@material-ui/core";
import { Colors } from "../../../styles/Colors";
import { ForwardModeFlag } from "../../../store/types/ServiceFeature";
import { CallForwardingRule } from "../../../components/Forms/Extensions/CallForwarding/CallForwardingForm.utils";
import {Delete, Edit} from '@material-ui/icons';
import {TFunction} from 'react-i18next';
import {Column} from 'react-table';
import i18n from "../../../services/i18n";
import Switch from '../../../components/Switch/Switch';
import { countRulesWeights } from '../../../components/Forms/Extensions/CallForwarding/FollowMeRulesList.utils';
import OverflowTooltip from '../../../components/OverflowTooltip/OverflowTooltip';
import CustomizedIconButton from '../../../components/IconButton/IconButton';

export const useStyles = makeStyles(() => ({
    callForwardingSwitchContainer: {
        padding: '8px 22px',
        minHeight: 37,
        height: 'fit-content'
    },
    switch: {
        marginTop: 0,
        marginBottom: 0,
        height: 'unset',
    },
    ringGroupSwitchLabel: {
        lineHeight: '20px',
        fontWeight: 400,
        fontSize: 16,
        textAlign: 'center',
        verticalAlign: 'middle',
        color: Colors.Text,
    },
    callForwardingModeContainer: {
        width: 'calc(100% - 44px)',
        height: 'fit-content',
        padding: '0px 22px',
        maxWidth: 440
    },
    callForwardingActionsContainer: {
        width: 'calc(100% - 44px)',
        height: 'fit-content',
        display: 'flex',
        flexDirection: "row",
        justifyContent: 'space-between',
        padding: '8px 22px 26px 22px'
    },
    primary: {
        backgroundColor: 'rgba(228, 235, 243, 1)',
        color: 'rgba(76, 134, 183, 1)',
        fontWeight: 500,
        fontSize: 16,
        lineHeight: '16px',
        letterSpacing: '0%',
        padding: '12px 20.5px'
    },
    primaryActivated: {
        color: 'rgba(228, 235, 243, 1)',
        backgroundColor: 'rgba(76, 134, 183, 1)',
    },
    disabled: {
        backgroundColor: Colors.Gray8 + " !important",
        color: 'rgba(0 ,0, 0, 0.7) !important',
        cursor: 'unset'
    },
    emptyListContainer: {
        width: 'calc(100% - 44px)',
        padding: '8px 22px',
        color: Colors.Gray5
    },
    removeDialogContainer: {},
    tableContainer: {
        backgroundColor: 'transparent',
        border: 0,
        maxWidth: 440,
        marginTop: 8,
        marginBottom: 8,
        '& .MuiTableRow-root': {
            backgroundColor: 'transparent',
        },

        '& .MuiTable-root': {
            backgroundColor: 'transparent',
        },

        '& table tr td.MuiTableCell-root:nth-child(2)': {
            justifyContent: 'center',
        },
        '& table tr td.MuiTableCell-root:nth-child(1)': {
            justifyContent: 'center',
        },
        '& table thead tr th:nth-child(4)': {
            justifyContent: 'center',
            textAlign: 'right',
        }
    },
    ruleContainer: {
        marginTop: 0,
        padding: '0px 22px',
    },
    rowClass: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        maxWidth: 'calc(480px - 48px)'
    },
    secondRowClass: {
        margin: '8px 0px'
    }
}));

export const generateColumns = (
    followMeMode: ForwardModeFlag,
    t: TFunction<string>,
    setFieldValue: (fieldName: string, value: any) => void,
    onEditRule: (rule: CallForwardingRule, index: number) => void,
    onRemoveRule: (rule: CallForwardingRule, index: number) => void,
): Column<CallForwardingRule>[] => {
    const columns: Column<CallForwardingRule>[] = [];

    columns.push({
        accessor: 'enabled',
        width: 1,
        Header: i18n.t<string>('common:active'),
        maxWidth: 80,
        minWidth: 80,
        Cell: function Cell(params) {
            return (
                <Switch
                    dataTestId="followme-rule-status"
                    dataQa="followme-rule-status"
                    checked={params.row.original.enabled}
                    onChange={(e) => {
                        setFieldValue(
                            `callForwardingRules.[${params.row.index}].enabled`,
                            e.target.checked,
                        );
                    }}
                />
            );
        },
    });

    return [
        ...columns,

        {
            accessor: 'description',
            Header: i18n.t<string>('common:name'),
            width: 1,
            maxWidth: 120,
            minWidth: 120,
            Cell: function Cell(params) {
                return (
                    <OverflowTooltip
                        text={params.row.original.description}
                        tooltip={params.row.original.description}
                    />
                );
            },
        },
        {
            accessor: 'number',
            Header: i18n.t<string>('common:phoneNumber'),
            width: 1,
            maxWidth: 130,
            minWidth: 130,
            Cell: function Cell(params) {
                const advancedInfo =
                    followMeMode === ForwardModeFlag.AdvancedForwarding
                        ? `${
                            params.row.original.sipProxy
                                ? `@${params.row.original.sipProxy} `
                                : ''
                        }(${params.row.original.transportProtocol})`
                        : null;

                return (
                    <div style={{width: 160}}>
                        <OverflowTooltip
                            text={params.row.original.number}
                            tooltip={params.row.original.number}
                        />
                        {!!advancedInfo && (
                            <OverflowTooltip
                                text={advancedInfo}
                                tooltip={advancedInfo}
                            />
                        )}
                    </div>
                );
            },
        },
        {
            accessor: 'id',
            Header: (<div>
                {t<string>('common:actions')}
            </div>),
            width: 1,
            maxWidth: 100,
            minWidth: 100,
            Cell: function Cell(params) {
                return (
                    <>
                        <CustomizedIconButton
                            data-testid="followme-rule-edit"
                            onClick={() =>
                                onEditRule(
                                    params.row.original,
                                    params.row.index,
                                )
                            }
                            tooltipText={t('common:edit')}
                        >
                            <Edit/>
                        </CustomizedIconButton>
                        <CustomizedIconButton
                            data-testid="followme-rule-remove"
                            onClick={() =>
                                onRemoveRule(
                                    params.row.original,
                                    params.row.index,
                                )
                            }
                            tooltipText={t('common:delete')}
                        >
                            <Delete/>
                        </CustomizedIconButton>
                    </>
                );
            },
        },
    ];
};